import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, ShieldCheck, AlertTriangle, Loader2,
  CheckCircle2, XCircle, AlertCircle, Wifi, Mail, MessageSquare,
  Activity, Database, Building2, Inbox, Bug, HelpCircle, Compass,
} from "lucide-react";
import AvidaLogo from "@/components/brand/AvidaLogo";
import IntegrationHealthTutorial from "@/components/admin/IntegrationHealthTutorial";
import IntegrationHealthGuidedTour from "@/components/admin/IntegrationHealthGuidedTour";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * IntegrationHealth — single-page status panel.
 *
 * Polls `/api/admin/integration-health` every 5s and renders pill-style
 * badges + last-success timestamps for each integration leg. Built for
 * coordinators / on-call engineers to answer "is the integration
 * actually working right now?" without grep'ing logs.
 */

function relativeTime(seconds) {
  if (seconds == null) return "never";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function Pill({ status, label }) {
  const colors = {
    green: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
    yellow: "bg-[#FEF3C7] text-[#854D0E] border-[#FDE68A]",
    red: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
    gray: "bg-[#F3F4F6] text-[#4B5563] border-[#E5E7EB]",
  };
  const icons = {
    green: <CheckCircle2 className="w-3.5 h-3.5" />,
    yellow: <AlertCircle className="w-3.5 h-3.5" />,
    red: <XCircle className="w-3.5 h-3.5" />,
    gray: <AlertCircle className="w-3.5 h-3.5" />,
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${colors[status] || colors.gray}`}
      data-testid={`pill-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {icons[status] || icons.gray}
      {label}
    </span>
  );
}

function StatusCard({ icon: Icon, title, status, primary, secondary, testId }) {
  return (
    <div
      className="rounded-xl border border-[#D6E3EF] bg-white p-5"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#3A5275]" />
          <h3 className="font-display text-[15px] font-bold text-[#0B1E3A]">
            {title}
          </h3>
        </div>
        <Pill status={status} label={status} />
      </div>
      <div className="text-[14px] text-[#0B1E3A] font-medium">{primary}</div>
      {secondary && (
        <div className="text-[12px] text-[#3A5275] mt-1">{secondary}</div>
      )}
    </div>
  );
}

function Counter({ label, value, testId, accent = false }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        accent && value > 0
          ? "border-[#FECACA] bg-[#FEF2F2]"
          : "border-[#D6E3EF] bg-white"
      }`}
      data-testid={testId}
    >
      <div className="text-[11px] uppercase tracking-wider text-[#3A5275] font-semibold mb-1">
        {label}
      </div>
      <div
        className={`font-display text-3xl font-bold ${
          accent && value > 0 ? "text-[#991B1B]" : "text-[#0B1E3A]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export default function IntegrationHealth() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStats, setTourStats] = useState(null);
  // First-time visitors get a subtle pulse on the Tutorial pill so they
  // notice it. Once they open it, we mark it seen and the pulse stops.
  const [tutorialSeen, setTutorialSeen] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("wi-pa-health-tutorial-seen") === "1";
  });
  const openTutorial = useCallback(() => {
    setTutorialOpen(true);
    if (!tutorialSeen) {
      try { localStorage.setItem("wi-pa-health-tutorial-seen", "1"); } catch {}
      setTutorialSeen(true);
    }
  }, [tutorialSeen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/integration-health`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData(j);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTourStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/wizard/tour-stats`);
      if (!r.ok) return;
      setTourStats(await r.json());
    } catch {
      /* tour stats are optional — keep main page working if endpoint fails */
    }
  }, []);

  useEffect(() => {
    load();
    loadTourStats();
  }, [load, loadTourStats]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  if (!data && !error) {
    return (
      <main className="min-h-screen bg-paper flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#3A5275]" />
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-paper"
      data-testid="integration-health-page"
    >
      <header className="bg-white border-b border-[#D6E3EF]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-3 text-[#0B1E3A] hover:text-[#0A3D79]"
          >
            <AvidaLogo iconOnly className="w-9 h-9" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#3A5275] font-semibold">
                Admin
              </div>
              <div className="font-display text-[16px] font-bold">
                Integration health
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openTutorial}
              className={`btn-ghost relative ${
                !tutorialSeen
                  ? "ring-2 ring-[#9333EA] ring-offset-1 animate-pulse"
                  : ""
              }`}
              data-testid="health-tutorial-btn"
              title="Tutorial — how to read this page"
            >
              <HelpCircle className="w-4 h-4" />
              Tutorial
              {!tutorialSeen && (
                <span
                  className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#9333EA]"
                  data-testid="health-tutorial-new-dot"
                />
              )}
            </button>
            <label
              className="flex items-center gap-2 text-[12px] text-[#3A5275]"
              data-testid="auto-refresh-toggle-label"
            >
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="auto-refresh-toggle"
              />
              Auto-refresh (5s)
            </label>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="btn-ghost"
              data-testid="health-refresh-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh
            </button>
            <Link
              to="/"
              className="btn-ghost"
              data-testid="health-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div
            className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-4 text-[14px] text-[#991B1B]"
            data-testid="health-error"
          >
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle className="w-4 h-4" /> Failed to load health data
            </div>
            <div className="mt-1">{error}</div>
          </div>
        )}

        {data && (
          <>
            {/* Top banner — overall mode */}
            <div
              className={`rounded-xl border-2 p-5 ${
                data.mode === "production"
                  ? "border-[#BBF7D0] bg-[#F0FDF4]"
                  : "border-[#FDE68A] bg-[#FFFBEB]"
              }`}
              data-testid="health-mode-banner"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3A5275]">
                    Integration mode
                  </div>
                  <div className="font-display text-2xl font-bold text-[#0B1E3A] mt-0.5">
                    {data.mode === "production" ? "PRODUCTION" : "PREVIEW"}
                  </div>
                  <div className="text-[12px] text-[#3A5275] mt-1">
                    {data.is_loopback
                      ? "Avida URL points back at this pod (loopback). Real cross-app requires AVIDA_CARE_URL pointed at the Avida pod."
                      : `Avida URL: ${data.config.avida_url || "(not set)"}`}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Pill
                    status={data.pills.avida_secrets}
                    label="Secrets"
                  />
                  <Pill status={data.pills.avida_url} label="Avida URL" />
                  <Pill status={data.pills.smtp} label="SMTP" />
                  <Pill status={data.pills.slack} label="Slack" />
                  <Pill status={data.pills.sentry} label="Sentry" />
                  {data.config.mock_enabled && (
                    <Pill status="yellow" label="Mock active" />
                  )}
                </div>
              </div>
            </div>

            {/* Last events */}
            <section>
              <h2 className="font-display text-[18px] font-bold text-[#0B1E3A] mb-3">
                Last events
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <StatusCard
                  icon={Inbox}
                  title="Avida → us · seed-draft"
                  status={data.last.seed_drafted ? "green" : "gray"}
                  primary={
                    data.last.seed_drafted
                      ? `${data.last.seed_drafted.pipeline_id || "—"} · ${relativeTime(data.last.seed_drafted.seconds_ago)}`
                      : "Never received"
                  }
                  secondary={
                    data.last.seed_drafted
                      ? `RN: ${data.last.seed_drafted.assigned_rn || "—"} · status ${data.last.seed_drafted.status || "—"}`
                      : "Waiting for first inbound webhook"
                  }
                  testId="last-seed-card"
                />
                <StatusCard
                  icon={Activity}
                  title="Us → Avida · completion ACK"
                  status={data.last.completion_synced ? "green" : "gray"}
                  primary={
                    data.last.completion_synced?.at
                      ? `${data.last.completion_synced.pipeline_id || "—"} · ${relativeTime(data.last.completion_synced.seconds_ago)}`
                      : "Never sent"
                  }
                  secondary={
                    data.last.completion_synced
                      ? `Avida response: ${data.last.completion_synced.avida_response_id || "—"}`
                      : "Awaiting first wizard completion"
                  }
                  testId="last-completion-card"
                />
                <StatusCard
                  icon={ShieldCheck}
                  title="Us → Avida · pa-outcome ACK"
                  status={data.last.pa_outcome_synced ? "green" : "gray"}
                  primary={
                    data.last.pa_outcome_synced
                      ? `${data.last.pa_outcome_synced.pipeline_id || "—"} (${data.last.pa_outcome_synced.outcome}) · ${relativeTime(data.last.pa_outcome_synced.seconds_ago)}`
                      : "Never sent"
                  }
                  secondary={
                    data.last.pa_outcome_synced
                      ? `Outcome: ${data.last.pa_outcome_synced.outcome}`
                      : "Awaiting first PA outcome record"
                  }
                  testId="last-outcome-card"
                />
                <StatusCard
                  icon={Bug}
                  title="Last failure"
                  status={data.last.failure ? "red" : "green"}
                  primary={
                    data.last.failure
                      ? `${data.last.failure.kind} · ${relativeTime(data.last.failure.seconds_ago)}`
                      : "No recent failures"
                  }
                  secondary={
                    data.last.failure
                      ? data.last.failure.last_error || "(no error message)"
                      : "Last 90 days clean"
                  }
                  testId="last-failure-card"
                />
              </div>
            </section>

            {/* Counters */}
            <section>
              <h2 className="font-display text-[18px] font-bold text-[#0B1E3A] mb-3">
                Live counters
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Counter
                  label="In-flight"
                  value={data.counts.in_flight_completions}
                  testId="counter-in-flight"
                />
                <Counter
                  label="Sync failed"
                  value={data.counts.sync_failed_drafts}
                  testId="counter-sync-failed"
                  accent
                />
                <Counter
                  label="Standalone pending"
                  value={data.counts.standalone_pending_reconcile}
                  testId="counter-standalone-pending"
                />
                <Counter
                  label="PA pending"
                  value={data.counts.pa_pending_outcome}
                  testId="counter-pa-pending"
                />
                <Counter
                  label="Agencies"
                  value={data.counts.organizations}
                  testId="counter-agencies"
                />
                <Counter
                  label="With recipients"
                  value={data.counts.organizations_with_alert_recipients}
                  testId="counter-with-recipients"
                />
              </div>
            </section>

            {/* Tour adoption */}
            {tourStats && (
              <section
                className="rounded-xl border border-[#D6E3EF] bg-white p-5"
                data-testid="tour-adoption-card"
              >
                <h2 className="font-display text-[15px] font-bold text-[#0B1E3A] mb-3 flex items-center gap-2">
                  <Compass className="w-4 h-4" /> Wizard tour adoption
                  <span className="ml-auto text-[11px] font-normal text-[#3A5275] uppercase tracking-wider">
                    Coordinator analytics
                  </span>
                </h2>
                <p className="text-[12.5px] text-[#3A5275] mb-3 leading-relaxed">
                  Tracks who launches the in-wizard guided tour, who completes
                  all 6 steps, and where RNs drop off. Use the drop-off
                  histogram to target follow-up training on the steps RNs
                  skip past most often.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <Counter
                    label="Starts"
                    value={tourStats.totals?.starts ?? 0}
                    testId="tour-counter-starts"
                  />
                  <Counter
                    label="Completes"
                    value={tourStats.totals?.completes ?? 0}
                    testId="tour-counter-completes"
                  />
                  <Counter
                    label="Skips"
                    value={tourStats.totals?.skips ?? 0}
                    testId="tour-counter-skips"
                  />
                  <Counter
                    label="Completion rate"
                    value={
                      tourStats.completion_rate == null
                        ? "—"
                        : `${Math.round(tourStats.completion_rate * 100)}%`
                    }
                    testId="tour-counter-completion-rate"
                  />
                </div>

                {Object.keys(tourStats.skip_histogram || {}).length > 0 && (
                  <div className="mb-4" data-testid="tour-dropoff-histogram">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5275] font-bold mb-2">
                      Drop-off by step
                    </div>
                    <div className="flex items-end gap-2 h-20">
                      {[0, 1, 2, 3, 4, 5].map((i) => {
                        const n = tourStats.skip_histogram[String(i)] || 0;
                        const max = Math.max(
                          1,
                          ...Object.values(tourStats.skip_histogram),
                        );
                        const h = Math.max(4, (n / max) * 64);
                        return (
                          <div
                            key={i}
                            className="flex-1 flex flex-col items-center gap-1"
                            data-testid={`tour-dropoff-bar-${i}`}
                          >
                            <div className="text-[10px] text-[#3A5275] font-mono">
                              {n}
                            </div>
                            <div
                              className="w-full bg-[#9333EA]/70 rounded-t"
                              style={{ height: `${h}px` }}
                            />
                            <div className="text-[10px] text-[#3A5275]">
                              Step {i + 1}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {tourStats.rns && tourStats.rns.length > 0 ? (
                  <div className="overflow-x-auto" data-testid="tour-rn-table">
                    <table className="w-full text-[12.5px]">
                      <thead>
                        <tr className="text-left text-[#3A5275] border-b border-[#E5E7EB]">
                          <th className="py-1.5 pr-3 font-semibold">RN</th>
                          <th className="py-1.5 pr-3 font-semibold text-center">Status</th>
                          <th className="py-1.5 pr-3 font-semibold text-center">Starts</th>
                          <th className="py-1.5 pr-3 font-semibold text-center">Completes</th>
                          <th className="py-1.5 pr-3 font-semibold text-center">Last skip step</th>
                          <th className="py-1.5 font-semibold">Last activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tourStats.rns.slice(0, 12).map((rn) => (
                          <tr
                            key={rn.rn_email}
                            className="border-b border-[#F3F4F6]"
                            data-testid={`tour-rn-row-${rn.rn_email}`}
                          >
                            <td className="py-1.5 pr-3 font-mono text-[#0B1E3A] truncate max-w-[220px]">
                              {rn.rn_email}
                            </td>
                            <td className="py-1.5 pr-3 text-center">
                              {rn.completed ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                                  <CheckCircle2 className="w-3 h-3" /> Done
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] text-[#854D0E] border border-[#FDE68A] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                                  Skipped
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 pr-3 text-center font-mono">
                              {rn.starts}
                            </td>
                            <td className="py-1.5 pr-3 text-center font-mono">
                              {rn.completes}
                            </td>
                            <td className="py-1.5 pr-3 text-center font-mono">
                              {rn.last_skipped_at_step == null
                                ? "—"
                                : `Step ${rn.last_skipped_at_step + 1}`}
                            </td>
                            <td className="py-1.5 text-[#3A5275] font-mono text-[11px]">
                              {rn.last_event_at
                                ? rn.last_event_at.replace("T", " ").slice(0, 19)
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    className="text-[12.5px] text-[#3A5275] italic"
                    data-testid="tour-rn-empty"
                  >
                    No tour activity yet. Once RNs start opening the
                    in-wizard guided tour, their adoption will show up here.
                  </div>
                )}
              </section>
            )}

            {/* Config detail */}
            <section
              className="rounded-xl border border-[#D6E3EF] bg-white p-5"
              data-testid="health-config-card"
            >
              <h2 className="font-display text-[15px] font-bold text-[#0B1E3A] mb-3 flex items-center gap-2">
                <Database className="w-4 h-4" /> Configuration
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
                <div className="flex justify-between border-b border-[#F3F4F6] py-1.5">
                  <dt className="text-[#3A5275]">Avida URL</dt>
                  <dd className="font-mono text-[#0B1E3A] truncate ml-3 max-w-[60%]">
                    {data.config.avida_url || "(not set)"}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[#F3F4F6] py-1.5">
                  <dt className="text-[#3A5275]">Public origin</dt>
                  <dd className="font-mono text-[#0B1E3A] truncate ml-3 max-w-[60%]">
                    {data.config.public_origin || "(not set)"}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[#F3F4F6] py-1.5">
                  <dt className="text-[#3A5275]">SMTP configured</dt>
                  <dd className="font-mono text-[#0B1E3A]">
                    {String(data.config.smtp_configured)}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[#F3F4F6] py-1.5">
                  <dt className="text-[#3A5275]">Slack configured</dt>
                  <dd className="font-mono text-[#0B1E3A]">
                    {String(data.config.slack_configured)}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[#F3F4F6] py-1.5">
                  <dt className="text-[#3A5275]">Sentry configured</dt>
                  <dd className="font-mono text-[#0B1E3A]">
                    {String(data.config.sentry_configured)}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[#F3F4F6] py-1.5">
                  <dt className="text-[#3A5275]">Loopback mode</dt>
                  <dd className="font-mono text-[#0B1E3A]">
                    {String(data.is_loopback)}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[#F3F4F6] py-1.5">
                  <dt className="text-[#3A5275]">Mock enabled</dt>
                  <dd className="font-mono text-[#0B1E3A]">
                    {String(data.config.mock_enabled)}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-[#F3F4F6] py-1.5">
                  <dt className="text-[#3A5275]">Updated</dt>
                  <dd className="font-mono text-[#0B1E3A] text-[11px]">
                    {data.now.replace("T", " ").slice(0, 19)}
                  </dd>
                </div>
              </dl>
            </section>
          </>
        )}
      </div>

      <IntegrationHealthTutorial
        open={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        onStartTour={() => setTourOpen(true)}
      />
      <IntegrationHealthGuidedTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
      />
    </main>
  );
}
